/*
 * Copyright (c) 2026 Ipivot Corporation.
 * All rights reserved.
 * This software is the confidential and proprietary information of
 *   Ipivot Corporation ("Confidential Information").
 */

/*
 * The one MapLibre wrapper every map in the app is built on.  It owns the map
 * instance and nothing else: creation, theming, resize and teardown.  Callers
 * get the live map through onMapReady and add their own sources and layers to
 * it, which keeps data concerns (clusters, live points, a single marker) out
 * of here.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Map as MapLibreMap, NavigationControl, setWorkerUrl } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
// @ts-ignore vite resolves this to the bundled worker's URL
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { useTheme } from 'styled-components';
import {
    buildBaseStyle, loadMapData, mapColorsFromTheme,
    COUNTRIES_50M, STATES_10M, CITIES_50M
} from '../utils/mapStyle';
import type { MapColors } from '../utils/mapStyle';

/*
 * MapLibre parses GeoJSON in a web worker that it loads from a URL derived
 * from its own module location.  Once the library is bundled that location no
 * longer has the worker file next to it, and the failure is silent: sources
 * simply never finish loading, so the map stays blank with no error.  Handing
 * vite the worker explicitly gets it bundled and served in both dev and
 * production, and keeps working across maplibre upgrades.
 */
setWorkerUrl(maplibreWorkerUrl);

type BaseMapProps = {
    countriesUrl?: string;
    // Both zoom gate themselves away at overview zooms, so they are on by
    // default; pass null to drop the layer and its fetch entirely
    statesUrl?: string | null;
    citiesUrl?: string | null;
    // Panels that want something other than the theme's map palette
    colors?: Partial<MapColors>;
    center?: [ number, number ];
    zoom?: number;
    // Opens on whatever zoom lays the world across the panel exactly once,
    // which only the panel's own width can decide, so zoom is ignored
    worldFit?: boolean;
    minZoom?: number;
    maxZoom?: number;
    interactive?: boolean;
    graticule?: boolean;
    navControl?: boolean;
    onMapReady?: (map: MapLibreMap | null) => void;
    style?: CSSProperties;
    className?: string;
};

function BaseMap({
    countriesUrl = COUNTRIES_50M,
    statesUrl = STATES_10M,
    citiesUrl = CITIES_50M,
    colors: colorOverrides,
    center = [ 0, 20 ],
    zoom = 0.8,
    worldFit = false,
    minZoom = 0,
    maxZoom = 16,
    interactive = true,
    graticule = true,
    navControl = true,
    onMapReady,
    style,
    className
}: BaseMapProps) {
    const holder = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<MapLibreMap | null>(null);
    const [ error, setError ] = useState<string>('');
    const theme: any = useTheme();
    // Undefined overrides must not shadow the theme values
    const colors = { ...mapColorsFromTheme(theme) };
    for (const [ k, v ] of Object.entries(colorOverrides || {})) {
        if (v) {
            (colors as any)[k] = v;
        }
    }

    // Held in refs so changing them never tears the map down and rebuilds it
    const readyRef = useRef(onMapReady);
    readyRef.current = onMapReady;
    const colorsRef = useRef(colors);
    colorsRef.current = colors;

    useEffect(() => {
        let cancelled = false;
        /*
         * The country outlines are the map; without them there is nothing to
         * show and the error surfaces.  States and cities are decoration, so a
         * missing or unbuilt asset drops that one layer and leaves the map
         * working rather than blanking it.
         */
        const decoration = (url: string | null | undefined) => (
            url
                ? loadMapData(url).catch((e: any) => {
                    console.error(`map layer ${url} failed to load`, e);
                    return null;
                })
                : Promise.resolve(null)
        );

        Promise.all([
            loadMapData(countriesUrl),
            decoration(statesUrl),
            decoration(citiesUrl)
        ]).then(([ countries, states, cities ]) => {
            if (cancelled || !holder.current) {
                return;
            }
            /*
             * MapLibre's world is 512px across at zoom 0 and doubles per zoom,
             * so this is the zoom that fills the panel with exactly one copy of
             * it.  A fixed zoom cannot do that: it leaves ocean margins on a
             * wide display and crops the edges on a narrow one.
             */
            const fitted = worldFit && holder.current.clientWidth
                ? Math.log2(holder.current.clientWidth / 512)
                : zoom;
            const map = new MapLibreMap({
                container: holder.current,
                style: buildBaseStyle(colorsRef.current, { countries, states, cities }, graticule),
                center,
                zoom: Math.max(minZoom, Math.min(maxZoom, fitted)),
                minZoom,
                maxZoom,
                interactive,
                attributionControl: false,
                // Country outlines carry no detail worth tilting or spinning for
                pitchWithRotate: false,
                dragRotate: false,
                touchZoomRotate: false
            });
            map.touchZoomRotate?.disableRotation();
            if (navControl && interactive) {
                map.addControl(new NavigationControl({ showCompass: false }), 'top-right');
            }
            mapRef.current = map;
            map.on('load', () => {
                if (!cancelled) {
                    readyRef.current?.(map);
                }
            });
        }).catch((e) => {
            if (!cancelled) {
                setError(String(e?.message || e));
            }
        });

        return () => {
            cancelled = true;
            readyRef.current?.(null);
            mapRef.current?.remove();
            mapRef.current = null;
        };
        // Rebuilding the map for a prop change would throw away the user's
        // current view, so the initial camera and mode are intentionally
        // read once at creation
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ countriesUrl, statesUrl, citiesUrl, graticule, interactive, navControl ]);

    // Theme switches repaint in place rather than recreating the map
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !map.isStyleLoaded()) {
            return;
        }
        const repaint = () => {
            if (map.getLayer('background')) {
                map.setPaintProperty('background', 'background-color', colors.bg);
            }
            if (map.getLayer('country-fill')) {
                map.setPaintProperty('country-fill', 'fill-color', colors.fill);
            }
            if (map.getLayer('country-line')) {
                map.setPaintProperty('country-line', 'line-color', colors.stroke);
            }
            if (map.getLayer('graticule') && colors.grat) {
                map.setPaintProperty('graticule', 'line-color', colors.grat);
            }
            if (map.getLayer('state-line')) {
                map.setPaintProperty('state-line', 'line-color', colors.state || colors.stroke);
            }
            if (map.getLayer('city-dot')) {
                map.setPaintProperty('city-dot', 'circle-color', colors.city || colors.stroke);
            }
            if (map.getLayer('city-label')) {
                map.setPaintProperty('city-label', 'text-color', colors.city || colors.stroke);
                // The halo knocks out the map behind the text, so it tracks
                // the background rather than the label colour
                map.setPaintProperty('city-label', 'text-halo-color', colors.bg);
            }
        };
        repaint();
    }, [ colors.bg, colors.fill, colors.stroke, colors.grat, colors.state, colors.city ]);

    /*
     * MapLibre only listens for window resizes, but these maps live in panels
     * and modals that resize on their own
     */
    useEffect(() => {
        if (!holder.current || typeof ResizeObserver === 'undefined') {
            return;
        }
        const ro = new ResizeObserver(() => mapRef.current?.resize());
        ro.observe(holder.current);
        return () => ro.disconnect();
    }, []);

    return (
        <div
            ref={holder}
            className={className}
            style={{ width: '100%', height: '100%', backgroundColor: colors.bg, ...style }}
        >
            {error ? (
                <div className="p-2 text-danger small">Map failed to load: {error}</div>
            ) : null}
        </div>
    );
}

export default BaseMap;
